import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

const ROLES = ['hr', 'intern', 'manager', 'executive'];

const STATUS_COLORS = {
  active:   { dot: 'var(--green)',  label: 'Active' },
  on_break: { dot: 'var(--amber)',  label: 'On Break' },
  leave:    { dot: 'var(--violet)', label: 'Leave' },
  offline:  { dot: 'var(--text-3)', label: 'Offline' },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'intern', requiredHoursPerDay: 8 });

  useEffect(() => { fetchUsers(); }, []);
  const fetchUsers = () => api.get('/users').then(r => setUsers(r.data));

  const openCreate = () => {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', role: 'intern', requiredHoursPerDay: 8 });
    setShowModal(true);
  };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, requiredHoursPerDay: u.requiredHoursPerDay });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const p = { ...form };
        if (!p.password) delete p.password;
        await api.put(`/users/${editUser._id}`, p);
        toast.success('User updated');
      } else {
        await api.post('/users', form);
        toast.success('User created');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u._id}`, { isActive: !u.isActive });
    fetchUsers();
    toast.success(u.isActive ? 'Deactivated' : 'Activated');
  };

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">{users.filter(u => u.isActive).length} active · {users.length} total</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>+ New User</button>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Req. Hours/Day</th>
              <th>Account</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const sc = STATUS_COLORS[u.status] || STATUS_COLORS.offline;
              return (
                <tr key={u._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div className="avatar" style={{ width: 30, height: 30, background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 12, fontWeight: 700 }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: 12.5, textTransform: 'capitalize', color: 'var(--accent)', fontWeight: 500 }}>{u.role}</span>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-2)' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
                      {sc.label}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{u.requiredHoursPerDay}h</td>
                  <td>
                    <span style={{ fontSize: 12, fontWeight: 500, color: u.isActive ? 'var(--green)' : 'var(--text-3)' }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="btn btn-xs btn-secondary" onClick={() => openEdit(u)}>Edit</button>
                      <button
                        className="btn btn-xs"
                        onClick={() => toggleActive(u)}
                        style={{
                          background: u.isActive ? 'var(--red-dim)' : 'var(--green-dim)',
                          color: u.isActive ? 'var(--red)' : 'var(--green)',
                          border: `1px solid ${u.isActive ? 'var(--red-border)' : 'var(--green-border)'}`,
                          borderRadius: 'var(--r-sm)', padding: '3px 8px', cursor: 'pointer',
                          fontSize: 12, fontFamily: 'inherit'
                        }}
                      >
                        {u.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={6}><div className="empty"><div className="empty-icon"></div><p>No users yet</p></div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>{editUser ? 'Edit User' : 'New User'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="John Doe" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="john@company.com" />
                </div>
                <div className="form-group">
                  <label>
                    Password{' '}
                    {editUser && <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(leave blank to keep)</span>}
                  </label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editUser} placeholder="••••••••" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Role</label>
                    <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                      {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Hours/Day</label>
                    <input type="number" min="1" max="24" value={form.requiredHoursPerDay} onChange={e => setForm(f => ({ ...f, requiredHoursPerDay: +e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>{editUser ? 'Update' : 'Create'}</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
